/*!
 * TrustBar - a compact strip of trust signals (licensed, insured, years, warranty...).
 * Vendly LLC. Rentable micro-app. Dependency-free, no build step.
 *
 * ---------------------------------------------------------------------------
 * USE
 *   <div id="trustbar"></div>
 *   <script>
 *     window.TrustBarConfig = {
 *       target: "#trustbar",
 *       accent: "#e8560a",
 *       items: [
 *         { type: "licensed" },
 *         { type: "insured" },
 *         { type: "years",   text: "18 years in business" },
 *         { type: "warranty" },
 *         { type: "local" },
 *         { type: "custom",  icon: "star", text: "5-star rated" }
 *       ]
 *     };
 *   </script>
 *   <script src="trustbar.js" defer></script>
 *
 *   Presets fill in a sensible label + icon; override any label with "text".
 *   Preset types: licensed, insured, years, warranty, local, guarantee, free-quote,
 *   family, veteran. Or type:"custom" with your own icon + text.
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  // Small inline SVG icons (single-path where possible), 24x24 viewBox.
  var ICONS = {
    shield: '<path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z"/>',
    check: '<path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z" opacity=".18"/><path d="M8.5 12.5l2.2 2.2 4.8-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    umbrella: '<path d="M12 3a9 9 0 0 1 9 9H3a9 9 0 0 1 9-9zm0 9v6a2 2 0 0 0 4 0"/>',
    clock: '<path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm.9 4h-1.8v5.4l4.2 2.5.9-1.5-3.3-2z"/>',
    badge: '<path d="M12 2l2.4 2.1 3.1-.5.9 3 2.6 1.7-1.4 2.9 1.4 2.9-2.6 1.7-.9 3-3.1-.5L12 22l-2.4-2.1-3.1.5-.9-3L3 14.2l1.4-2.9L3 8.4l2.6-1.7.9-3 3.1.5z"/>',
    pin: '<path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"/>',
    tag: '<path d="M20 12l-8 8-9-9V4h7l10 8zM7.5 7.5A1.5 1.5 0 1 0 7.5 5a1.5 1.5 0 0 0 0 2.5z"/>',
    heart: '<path d="M12 21s-7-4.4-9.3-8.6C1.2 9.4 2.7 6 6 6c2 0 3.2 1.2 4 2.3C10.8 7.2 12 6 14 6c3.3 0 4.8 3.4 3.3 6.4C19 16.6 12 21 12 21z"/>',
    star: '<path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7L12 2z"/>'
  };

  var PRESETS = {
    licensed:   { icon: "check",    text: "Licensed" },
    insured:    { icon: "umbrella", text: "Insured" },
    years:      { icon: "clock",    text: "Years in business" },
    warranty:   { icon: "badge",    text: "Workmanship warranty" },
    guarantee:  { icon: "badge",    text: "Satisfaction guaranteed" },
    local:      { icon: "pin",      text: "Locally owned" },
    "free-quote": { icon: "tag",    text: "Free quotes" },
    family:     { icon: "heart",    text: "Family owned" },
    veteran:    { icon: "star",     text: "Veteran owned" }
  };

  var DEFAULTS = { target: "#trustbar", accent: "#e8560a", items: [] };

  function merge(user) {
    var c = {};
    for (var k in DEFAULTS) c[k] = DEFAULTS[k];
    for (var j in user) if (user.hasOwnProperty(j)) c[j] = user[j];
    return c;
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch];
    });
  }

  // Hardening (2026-07-11): accent is injected into a <style> block. It can never
  // break out to HTML (set via textContent), but strip characters not used in a
  // color token so a hostile config value cannot inject extra CSS rules
  // (e.g. "red;}body{display:none"). Keeps #hex, rgb()/hsl(), and named colors.
  function safeColor(c) {
    var v = String(c == null ? "" : c).slice(0, 40).replace(/[^#a-zA-Z0-9(),.%\s-]/g, "");
    return v || "#e8560a";
  }

  function injectCss(accent) {
    accent = safeColor(accent);
    if (document.getElementById("trustbar-css")) return;
    var css =
      ".tb{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
      "display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:10px 22px;" +
      "padding:14px 16px;border-top:1px solid #ececec;border-bottom:1px solid #ececec;background:#fff}" +
      ".tb-item{display:flex;align-items:center;gap:8px;font-size:14px;font-weight:600;color:#14140f}" +
      ".tb-item svg{width:20px;height:20px;flex:0 0 auto;fill:" + accent + ";color:" + accent + "}";
    var el = document.createElement("style");
    el.id = "trustbar-css";
    el.textContent = css;
    document.head.appendChild(el);
  }

  function iconSvg(name) {
    var path = ICONS[name] || ICONS.check;
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' + path + "</svg>";
  }

  function render(container, cfg) {
    var bar = document.createElement("div");
    bar.className = "tb";
    var html = "";
    for (var i = 0; i < cfg.items.length; i++) {
      var it = cfg.items[i] || {};
      var preset = PRESETS[it.type];
      var icon = it.icon || (preset && preset.icon) || "check";
      var text = it.text || (preset && preset.text) || "";
      if (!text) continue;
      html += '<span class="tb-item">' + iconSvg(icon) + "<span>" + escapeHtml(text) + "</span></span>";
    }
    bar.innerHTML = html;
    container.innerHTML = "";
    container.appendChild(bar);
  }

  function init(userCfg) {
    var cfg = merge(userCfg || {});
    injectCss(cfg.accent);
    var container = document.querySelector(cfg.target);
    if (!container) {
      if (window.console) console.warn("[TrustBar] target not found:", cfg.target);
      return;
    }
    render(container, cfg);
  }

  window.TrustBar = { init: init };
  function autostart() { if (window.TrustBarConfig) init(window.TrustBarConfig); }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autostart);
  } else {
    autostart();
  }
})();
